import type {
  ShareSecurity,
  ShareSecurityLevel,
  WelcomeMatConfig,
} from '@carboncopyco/types';
import {
  Checkbox,
  InlineLink,
  TextBase,
  TextBoxEditorTitle,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useUpdateEffect } from 'ahooks';
import { Field, type FieldProps, useFormikContext } from 'formik';
import { useMemo, useRef } from 'react';

const disabledEmailCheckboxMessage: Record<ShareSecurityLevel, string> = {
  internal: 'To verify internal visitors, this field must be checked.',
  restricted: 'To verify visitor email, this field must be checked.',
  email_domain: 'To verify visitor email-domain, this field must be checked.',
  anyone: 'When "add new leads" is on, visitor email is required.',
};

type WelcomeMatFieldProps = {
  handlePreviewCoverPage: () => void;
  allowWelcomeMatConfigChange?: boolean;
};

export const getDefaultWelcomeMatConfigByModalState = <
  S extends {
    security: ShareSecurity;
    salesforceLeads: boolean;
    hubspotLeads: boolean;
  },
>({
  values,
}: {
  values: S;
}) => {
  const {
    security: { level },
    salesforceLeads,
    hubspotLeads,
  } = values;

  return level === 'internal'
    ? salesforceLeads || hubspotLeads
      ? {
          requireName: false,
          requireEmail: false,
        }
      : {
          requireName: false,
          requireEmail: true,
        }
    : {
        requireName: true,
        requireEmail: true,
      };
};

export const WelcomeMatField = <
  S extends {
    cover: WelcomeMatConfig;
    security: {
      level: ShareSecurityLevel;
      filterEmailDomains?: boolean;
    };
    salesforceLeads: boolean;
    hubspotLeads: boolean;
    skipWelcome: boolean;
  },
>({
  handlePreviewCoverPage,
  allowWelcomeMatConfigChange,
}: WelcomeMatFieldProps) => {
  const { values, setFieldValue, setValues } = useFormikContext<S>();
  const infoRef = useRef(null);
  const infoRefName = useRef(null);

  useUpdateEffect(() => {
    setValues({
      ...values,
      skipWelcome: false,
      cover: getDefaultWelcomeMatConfigByModalState({
        values,
      }),
    });
  }, [
    values.security.level,
    values.hubspotLeads,
    values.salesforceLeads,
    values.security.filterEmailDomains,
  ]);

  const isSkipWelcomeCheckboxDisabled = useMemo(
    () =>
      values.security.level !== 'anyone' ||
      values.hubspotLeads ||
      values.salesforceLeads ||
      values.security.filterEmailDomains ||
      Object.values(values.cover).some((value) => value),
    [values]
  );

  const isNameCheckboxDisabled = useMemo(() => {
    const leads = values.hubspotLeads || values.salesforceLeads;
    const map = {
      internal: leads,
      restricted: leads,
      email_domain: leads,
      anyone: leads && !values.skipWelcome,
    };
    return map[values.security.level];
  }, [values]);

  const isEmailCheckboxDisabled = useMemo(() => {
    const map = {
      internal: true,
      restricted: true,
      email_domain: true,
      anyone:
        !values.skipWelcome &&
        (values.hubspotLeads ||
          values.salesforceLeads ||
          values.security.filterEmailDomains),
    };
    return map[values.security.level];
  }, [values]);

  return (
    <div className="flex flex-col gap-3 p-2 items-start">
      <TextBoxEditorTitle label={'cover page'.toUpperCase()} />
      {allowWelcomeMatConfigChange && (
        <Field
          type="checkbox"
          name="skipWelcome"
        >
          {({ field: { checked, ...rest } }: FieldProps) => (
            <Checkbox
              {...rest}
              onChange={() => {
                setFieldValue('skipWelcome', !values.skipWelcome);
              }}
              disabled={isSkipWelcomeCheckboxDisabled}
              checked={!values.skipWelcome}
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Use cover page"
                />
              }
            />
          )}
        </Field>
      )}
      <Field
        type="checkbox"
        name="cover.requireName"
      >
        {({ field: { checked, ...rest } }: FieldProps) => (
          <div ref={infoRefName}>
            <Checkbox
              {...rest}
              name="cover.requireName"
              onChange={() => {
                setValues({
                  ...values,
                  cover: {
                    ...values.cover,
                    requireName: !checked,
                  },
                  skipWelcome: !checked ? false : values.skipWelcome,
                });
              }}
              checked={values.cover.requireName}
              disabled={isNameCheckboxDisabled}
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Require visitor's name"
                />
              }
            />
            {isNameCheckboxDisabled && (
              <Tooltip
                delay={[800, 0]}
                placement="top"
                trigger="mouseenter"
                hideOnClick="toggle"
                anchorRef={infoRefName}
                noArrow
              >
                <div className="py-2 px-2.5">
                  <TextCaption label="To process visitor data, this field must be checked" />
                </div>
              </Tooltip>
            )}
          </div>
        )}
      </Field>
      <Field
        type="checkbox"
        name="cover.requireEmail"
      >
        {({ field: { checked, ...rest } }: FieldProps) => (
          <>
            <div ref={infoRef}>
              <Checkbox
                {...rest}
                onChange={() => {
                  setValues({
                    ...values,
                    cover: {
                      ...values.cover,
                      requireEmail: !checked,
                    },
                    skipWelcome: !checked ? false : values.skipWelcome,
                  });
                }}
                checked={values.cover.requireEmail}
                disabled={isEmailCheckboxDisabled}
                label={
                  <TextBase
                    color="text-ds-natural-600"
                    label="Require visitor's email"
                  />
                }
              />
            </div>
            {isEmailCheckboxDisabled && (
              <Tooltip
                delay={[800, 0]}
                placement="top"
                trigger="mouseenter"
                hideOnClick="toggle"
                anchorRef={infoRef}
                noArrow
              >
                <div className="py-2 px-2.5">
                  <TextCaption
                    label={disabledEmailCheckboxMessage[values.security.level]}
                  />
                </div>
              </Tooltip>
            )}
          </>
        )}
      </Field>
      <div className="h-[32px]">
        {!values.skipWelcome && (
          <div className="flex flex-row items-center gap-1">
            <TextCaption label="Before entering the demo, visitors would see" />
            <div className="mb-1">
              <InlineLink
                onClick={handlePreviewCoverPage}
                label="this cover"
                color="text-ds-natural"
                fontSize="text-ds-200"
                fontWeight="font-ds-light"
                lineHeight="leading-ds-200"
                letterSpacing="tracking-ds-tight"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
